/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action, StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { refreshViews } from './refreshViews.tsx';
import { modifyView, findViewBy } from './updateContainerState.tsx';

export const refreshView =
	(viewUUID: string): Action<State, Props> =>
	async (
		{ setState, getState, dispatch }: StoreActionApi<State>,
		{ navigationRemote, cloudId, containerId, onFailure, onSuccess }: Props,
	) => {
		if (
			!cloudId ||
			!containerId ||
			!navigationRemote ||
			!getState().containers[containerId]?.initialized
		) {
			return;
		}

		const { view: viewToRefresh } = findViewBy(
			getState().containers[containerId],
			(view) => view.id === viewUUID,
		);

		if (
			getState().containers[containerId]?.isRefreshing ||
			getState().containers[containerId]?.numberOfRefreshBlocks > 0 ||
			viewToRefresh?.isRefreshing
		) {
			onSuccess?.('refreshView.conflict');
			// To avoid multiple refreshes, we schedule a whole tree refresh
			return dispatch(refreshViews());
		}

		setState(
			modifyView(getState(), containerId, (viewToModify) => {
				if (viewToModify.id === viewUUID) {
					return {
						...viewToModify,
						isRefreshing: true,
					};
				}
				return viewToModify;
			}),
		);

		const lastRefreshViewTime = Date.now();

		try {
			const remoteView = await navigationRemote.fetchView(viewUUID);

			if (
				getState().containers[containerId]?.lastRefreshBlockTime > lastRefreshViewTime ||
				getState().containers[containerId]?.lastRefreshViewsTime > lastRefreshViewTime ||
				getState().containers[containerId]?.numberOfRefreshBlocks > 0 ||
				getState().containers[containerId]?.isRefreshing
			) {
				onSuccess?.('refreshView.outdated');
				setState(
					modifyView(getState(), containerId, (viewToModify) => {
						if (viewToModify.id === viewUUID) {
							return {
								...viewToModify,
								isRefreshing: false,
							};
						}
						return viewToModify;
					}),
				);
				return dispatch(refreshViews());
			}

			const { view: viewToUpdate } = findViewBy(
				getState().containers[containerId],
				(view) => view.id === viewUUID,
			);

			if (!viewToUpdate) {
				return;
			}

			setState(
				modifyView(getState(), containerId, (viewToModify) => {
					if (viewToModify.id === viewUUID) {
						return {
							...viewToModify,
							name: remoteView.name,
							emoji: remoteView.emoji,
							isRefreshing: false,
						};
					}
					return viewToModify;
				}),
			);

			onSuccess?.('refreshView');
		} catch (err) {
			setState(
				modifyView(getState(), containerId, (viewToModify) => {
					if (viewToModify.id === viewUUID) {
						return {
							...viewToModify,
							isRefreshing: false,
						};
					}
					return viewToModify;
				}),
			);
			onFailure?.(err instanceof Error ? err : new Error('Unknown error'), 'refreshView');
		}
	};
